import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CircleDashed, CheckCircle2, Ban, Loader2, ShieldCheck } from "lucide-react";
import { useGrowSignals, useSetGrowSignal, useVerifyGrowSignal } from "@/hooks/useApi";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";
import type { GrowSignalStatus } from "@/types/api";

const STATUS_META: Record<GrowSignalStatus, { tone: string; icon: React.ElementType }> = {
  pending: { tone: "bg-[rgba(255,200,61,0.15)] text-[#b57a00] border-[rgba(255,200,61,0.3)]", icon: CircleDashed },
  activated: { tone: "bg-[rgba(39,161,85,0.1)] text-[#27a155] border-[rgba(39,161,85,0.25)]", icon: CheckCircle2 },
  unavailable: { tone: "bg-muted text-muted-foreground border-transparent", icon: Ban },
};

/** Curadoria dos sinais clean-core do GROW (S-043.7 / #323).
 *
 * "Activated" só é setado pela VERIFICAÇÃO ao vivo (o cenário responde no GROW);
 * nunca à mão. "Unavailable" é decisão explícita do usuário (custo/licenciamento),
 * com motivo. O reprocess considera os 'activated' (parte B). */
export function GrowSignalsPanel({ projectId }: { projectId: string }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { data } = useGrowSignals(projectId);
  const setSignal = useSetGrowSignal(projectId);
  const verify = useVerifyGrowSignal(projectId);

  const [svc, setSvc] = useState<Record<string, string>>({});
  const [reason, setReason] = useState<Record<string, string>>({});

  const signals = data?.signals ?? [];

  const statusLabel = (s: GrowSignalStatus) =>
    s === "activated" ? t("Activated") : s === "unavailable" ? t("Unavailable") : t("Pending");

  // Rótulo por categoria (fallback: a própria chave). Ordem: clean-core primeiro,
  // depois integração/custom, depois quaisquer outras na ordem de aparição.
  const categoryLabel = (c: string) =>
    ({ clean_core: t("Clean-core"), integration: t("Integration"), custom: t("Custom") })[c] ?? c;
  const CATEGORY_ORDER = ["clean_core", "integration", "custom"];
  const groups = signals.reduce<Record<string, typeof signals>>((acc, s) => {
    (acc[s.category || "custom"] ??= []).push(s);
    return acc;
  }, {});
  const orderedCats = [
    ...CATEGORY_ORDER.filter((c) => groups[c]),
    ...Object.keys(groups).filter((c) => !CATEGORY_ORDER.includes(c)),
  ];

  const doVerify = (scenarioId: string, fallbackPath?: string | null) => {
    const servicePath = (svc[scenarioId] ?? fallbackPath ?? "").trim();
    if (!servicePath) {
      toast({ title: t("Enter the Service URL from the arrangement first"), variant: "destructive" });
      return;
    }
    verify.mutate(
      { scenarioId, servicePath },
      {
        onSuccess: (r) =>
          toast({
            title: r.verified ? t("Signal activated") : t("Not active in GROW yet"),
            description: r.message,
            variant: r.verified ? undefined : "destructive",
          }),
        onError: () => toast({ title: t("Verification failed"), variant: "destructive" }),
      },
    );
  };

  const renderSignal = (s: (typeof signals)[number]) => {
    const meta = STATUS_META[s.status];
    const Icon = meta.icon;
    const busy = verify.isPending || setSignal.isPending;
    return (
            <div key={s.scenario_id} className="rounded-lg border border-[#e8ebf0] p-3 space-y-2">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-[#08254c] truncate">{s.name}</span>
                    <span className="font-mono text-[10px] text-[#7b8799]">{s.scenario_id}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">{s.description}</p>
                  {s.status === "activated" && s.service_path && (
                    <p className="text-[11px] font-mono text-[#27a155] mt-1 truncate">{s.service_path}</p>
                  )}
                  {s.status === "unavailable" && s.reason && (
                    <p className="text-xs text-destructive mt-1">{t("Reason")}: {s.reason}</p>
                  )}
                </div>
                <span className={"inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border uppercase tracking-[0.06em] flex-shrink-0 " + meta.tone}>
                  <Icon className="w-3 h-3" /> {statusLabel(s.status)}
                </span>
              </div>

              {s.status !== "unavailable" ? (
                <div className="flex items-center gap-2">
                  <Input
                    className="h-8 text-xs"
                    placeholder={t("/sap/… (relative to the Base URL)")}
                    defaultValue={s.service_path ?? ""}
                    onChange={(e) => setSvc((m) => ({ ...m, [s.scenario_id]: e.target.value }))}
                  />
                  <Button size="sm" className="h-8 whitespace-nowrap" disabled={busy} onClick={() => doVerify(s.scenario_id, s.service_path)}>
                    {verify.isPending ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <ShieldCheck className="w-3.5 h-3.5 mr-1" />}
                    {t("Verify in GROW")}
                  </Button>
                </div>
              ) : null}

              <div className="flex items-center gap-2">
                {s.status !== "unavailable" ? (
                  <>
                    <Input
                      className="h-8 text-xs"
                      placeholder={t("Reason (cost, licensing, ...)")}
                      onChange={(e) => setReason((m) => ({ ...m, [s.scenario_id]: e.target.value }))}
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 whitespace-nowrap"
                      disabled={busy}
                      onClick={() =>
                        setSignal.mutate({
                          scenarioId: s.scenario_id,
                          status: "unavailable",
                          reason: reason[s.scenario_id] || undefined,
                        })
                      }
                    >
                      {t("Mark unavailable")}
                    </Button>
                  </>
                ) : (
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-8"
                    disabled={busy}
                    onClick={() => setSignal.mutate({ scenarioId: s.scenario_id, status: "pending" })}
                  >
                    {t("Reset to pending")}
                  </Button>
                )}
              </div>
            </div>
    );
  };

  return (
    <div className="space-y-4">
      <p className="text-xs text-muted-foreground">
        {t("Communication scenarios to activate in GROW so the Architect and Code Companion anchor decisions on real data.")}
        {data ? ` ${data.pending_count}/${data.total} ${t("pending")}.` : ""}
      </p>
      {orderedCats.length === 0 ? (
        <p className="text-xs text-muted-foreground">{t("No scenarios available.")}</p>
      ) : (
        orderedCats.map((cat) => (
          <div key={cat} className="space-y-2">
            <h5 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-[#7b8799]">
              {categoryLabel(cat)}
            </h5>
            {groups[cat].map(renderSignal)}
          </div>
        ))
      )}
    </div>
  );
}
